import { Stack, router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';

import { IStore } from '@rbi-ctg/store';
import { GraphQLErrorMessages } from 'enums/graphql';
import {
  DeliveryStatus,
  IDeliveryFee,
  useDeliveryRestaurantLazyQuery,
} from 'generated/rbi-graphql';
import usePlaceIdDetails from 'hooks/geolocation/use-place-id-details';
import useDialogModal from 'hooks/use-dialog-modal';
import {
  DeliveryStoreStatusToStoreStatusMap,
  IGetClosestAvailableDeliveryRestaurantResult,
  useQueryClosestAvailableDeliveryRestaurant,
} from 'remote/api/restaurants';
import { useAuthContext } from 'state/auth';
import { useCRMEventsContext } from 'state/crm-events';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useNetworkContext } from 'state/network';
import { ServiceMode, useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { convertMilesToMeters } from 'utils/distance';
import { platform } from 'utils/environment';
import { IPlaceData } from 'utils/geolocation';
import { routes } from 'utils/routing';

import { useUserRecentOrder } from '../../state/order/hooks/use-user-recent-order';

import { DeliveryAddresses } from './delivery-addresses';
import { DeliveryUnavailable, DeliveryUnavailableError } from './delivery-unavailable';
import DeliveryAddressForm from './new-address-form/delivery-address-form';
import { addressReducer } from './reducer';
import { ActionStatus, SelectedDeliveryAddress } from './types';
import { getAddressLabel } from './utils';

export const DeliveryAddressContainer = () => {
  const { formatMessage } = useIntl();
  const params = useLocalSearchParams<{ back: string; reorderOrderId?: string }>();

  const [state, dispatch] = useReducer(addressReducer, {
    error: null,
    address: null,
    status: ActionStatus.IDLE,
  });

  // Callback to add reorder items to the cart after selecting a store
  const { handleReorderAfterStoreSelect } = useUserRecentOrder({
    singleOrderId: params.reorderOrderId,
  });

  const { user } = useAuthContext();
  const [createAddressView, setCreateAddressView] = useState(false);
  const [queryDeliveryRestaurant, { data: deliveryRestaurantData }] =
    useDeliveryRestaurantLazyQuery({ fetchPolicy: 'no-cache' });
  const [queryRestaurantsFromBackend, storeData, deliveryQuoteError] =
    useQueryClosestAvailableDeliveryRestaurant();
  const { connection } = useNetworkContext();
  const { updateDeliveryFees, setShowFeeWarning } = useServiceModeContext();
  const getDetails = usePlaceIdDetails();
  const enableDeliveryFeeDialogOnDeliveryAddressSelect = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_FEE_DIALOG_ON_DELIVERY_ADDRESS_SELECT)
  );

  const { logDeliveryStoreAssigned } = useCRMEventsContext();

  const order = useOrderContext();
  const [AlertDialog, openAlert, alertMessage] = useDialogModal({
    onConfirm: () => router.navigate(`${routes.account}/info`),
    modalAppearanceEventMessage: 'Alert: Payment Failed',
  });
  const enableUserSavedDeliveryAddressPhone = useFlag(
    LaunchDarklyFlag.ENABLE_USER_SAVED_DELIVERY_ADDRESS_PHONE
  );
  const enableConsolidatedDeliveryRestaurantLogic = useFlag(
    LaunchDarklyFlag.ENABLE_CONSOLIDATED_DELIVERY_RESTAURANT_BACKEND_LOGIC
  );
  const deliveryRadius = useFlag(LaunchDarklyFlag.DELIVERY_RADIUS_IN_MILES);
  const shouldEnableUserSavedDeliveryAddressPhone = enableUserSavedDeliveryAddressPhone && !!user;

  const showDeliveryAddresses =
    shouldEnableUserSavedDeliveryAddressPhone && Boolean(user?.details?.deliveryAddresses?.length);
  const {
    setDeliveryAddress,
    setDeliveryInstructions,
    setOrderPhoneNumber,
    setQuoteId,
    selectStore,
    selectServiceMode,
  } = order;

  const handleQuoteResult = useCallback(
    async ({
      store,
      deliveryQuote,
      storeStatus,
      nextEarliestOpen,
    }: IGetClosestAvailableDeliveryRestaurantResult) => {
      // if store available, route to store
      if (store && deliveryQuote === DeliveryStatus.QUOTE_SUCCESSFUL) {
        return selectStore(
          store,
          async () => {
            if (state.address) {
              const { address, shouldSaveAddress, deliveryInstructions, phoneNumber, coordinates } =
                state.address;

              setDeliveryAddress({
                ...address,
                shouldSave: shouldSaveAddress,
                latitude: coordinates?.lat,
                longitude: coordinates?.lng,
              });

              setDeliveryInstructions(deliveryInstructions);
              setOrderPhoneNumber(phoneNumber);
            }

            const serviceMode = ServiceMode.DELIVERY;
            selectServiceMode(serviceMode);
            if (params.reorderOrderId) {
              handleReorderAfterStoreSelect();
            } else {
              router.replace(params.back ?? routes.menu);
            }
          },
          ServiceMode.DELIVERY
        );
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_UNAVAILABLE) {
        return dispatch({ type: ActionStatus.QUOTE_UNAVAILABLE });
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_ERROR) {
        return dispatch({
          type: ActionStatus.ERROR,
          error: {
            message: formatMessage({ id: 'pleaseTryAgainLater' }),
            modalAppearanceEventMessage: 'Error: Delivery Quote Error',
          },
        });
      }

      // otherwise present current store status modal
      router.navigate({
        pathname: routes[storeStatus],
        params: { back: params.back, nextEarliestOpen: nextEarliestOpen?.toISOString() },
      });
    },
    [
      params,
      state.address,
      selectStore,
      selectServiceMode,
      setDeliveryAddress,
      setDeliveryInstructions,
      setOrderPhoneNumber,
      handleReorderAfterStoreSelect,
      formatMessage,
    ]
  );

  useEffect(() => {
    const restaurant = deliveryRestaurantData?.deliveryRestaurant?.restaurant || storeData?.store;
    if (!restaurant) {
      return;
    }

    logDeliveryStoreAssigned({
      restaurantId: restaurant._id || '',
      storeId: restaurant.storeId || '',
      restaurantAddress: restaurant.physicalAddress?.address1 || '',
      restaurantZip: restaurant.physicalAddress?.postalCode || '',
      restaurantCity: restaurant.physicalAddress?.city || '',
      restaurantState: restaurant.physicalAddress?.stateProvince || '',
      restaurantCountry: restaurant.physicalAddress?.country || '',
      quotedFeeCents: deliveryRestaurantData?.deliveryRestaurant.fees?.totalFeeCents ?? 0,
    });
  }, [deliveryRestaurantData, storeData, logDeliveryStoreAssigned]);

  useEffect(() => {
    if (deliveryQuoteError && deliveryQuoteError === GraphQLErrorMessages.INVALID_PHONE_NUMBER) {
      openAlert({
        body: formatMessage({ id: 'areDeliveryDetailsValid' }),
        buttonLabel: formatMessage({ id: 'addPhoneNumber' }),
        heading: formatMessage({ id: 'deliveryPhoneNumberError' }),
      });
      return;
    }

    if (storeData) {
      handleQuoteResult(storeData);
    }

    if (deliveryRestaurantData?.deliveryRestaurant && enableConsolidatedDeliveryRestaurantLogic) {
      const { quote, restaurant, storeStatus, nextEarliestOpen, quoteId, fees } =
        deliveryRestaurantData?.deliveryRestaurant;

      if (fees?.totalFeeCents) {
        if (enableDeliveryFeeDialogOnDeliveryAddressSelect) {
          setShowFeeWarning(true);
        }

        updateDeliveryFees({
          deliveryTotalFee: fees.totalFeeCents,
          fees: fees?.itemizedFees ? (fees.itemizedFees as IDeliveryFee[]) : [],
        });
      }

      setQuoteId(quoteId);

      //Note: Quote ID was not added into the object to avoid creating a conflict with the existing query data being returned.
      handleQuoteResult({
        deliveryQuote: quote,
        store: restaurant as unknown as IStore,
        storeStatus: DeliveryStoreStatusToStoreStatusMap[storeStatus],
        nextEarliestOpen: nextEarliestOpen ? new Date(nextEarliestOpen) : undefined,
      });
    }

    /*
     * TODO: `handleQuoteResult` is a created by `useCallback`. It's updated
     * whenever `selectServiceMode` is updated, which is also created by
     * `useCallback`. `selectServiceMode` is updated whenever the `serviceMode`
     * state variable is changed. `selectServiceMode` is called in
     * `handleQuoteResult` causing an infinite render loop.
     *
     * Leaving `handleQuoteResult` out of the dependency array solves the issue
     * for now, but might needs a closer look as we organize state more effectively.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableConsolidatedDeliveryRestaurantLogic,
    deliveryRestaurantData,
    deliveryQuoteError,
    storeData,
  ]);

  const completeDelivery = async (data: SelectedDeliveryAddress) => {
    if (state.status === ActionStatus.PENDING) {
      return;
    }

    if (!data.address) {
      return dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Missing address information for delivery' },
      });
    }

    const {
      address,
      phoneNumber,
      coordinates: selectedCoordinates,
      placeId: selectedPlaceId,
    } = data;

    dispatch({ type: ActionStatus.PENDING, address: data });

    try {
      const placeId = selectedPlaceId;

      // TODO: RN - In very rare situations you could end up here without a placeId. Add an Address to PlaceId lookup back
      // if (!selectedCoordinates && !selectedPlaceId) {
      //   // only get the placeId if we need it to look up coordinates
      //   placeId = await getPlaceId(getAddressLabel(data));
      // }

      // TODO: RN - Is this extra place lookup really needed?
      const { coordinates } = selectedCoordinates
        ? { coordinates: selectedCoordinates }
        : await getDetailsPlaceData(placeId);

      if (!coordinates) {
        throw new Error('Unable to get coordinates from selected address');
      }

      if (enableConsolidatedDeliveryRestaurantLogic) {
        queryDeliveryRestaurant({
          variables: {
            dropoff: {
              ...address,
              latitude: coordinates.lat,
              longitude: coordinates.lng,
              phoneNumber,
            },
            searchRadius: convertMilesToMeters(deliveryRadius),
            platform: platform(),
          },
        });
      } else {
        queryRestaurantsFromBackend(
          phoneNumber,
          deliveryRadius,
          {
            connection,
            userLat: coordinates.lat,
            userLng: coordinates.lng,
          },
          address
        );
      }
    } catch (error) {
      dispatch({
        type: ActionStatus.ERROR,
        // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
        error: { message: 'Error using delivery address', error },
      });
    }
  };

  const getDetailsPlaceData = useCallback(
    (pId: string | undefined): Promise<IPlaceData> => {
      return getDetails(pId).then(placeData => {
        if (placeData) {
          return Promise.resolve(placeData);
        }

        return Promise.reject(placeData);
      });
    },
    [getDetails]
  );
  if (state.status === ActionStatus.ERROR) {
    return (
      <DeliveryUnavailableError onChooseNewAddress={() => dispatch({ type: ActionStatus.IDLE })} />
    );
  }
  return (
    <>
      {state.status === ActionStatus.QUOTE_UNAVAILABLE ? (
        <DeliveryUnavailable
          address={state.address ? getAddressLabel(state.address) : ''}
          coordinates={state?.address?.coordinates}
          onChooseNewAddress={() => dispatch({ type: ActionStatus.IDLE })}
        />
      ) : (
        <>
          <Stack.Screen options={{ title: formatMessage({ id: 'deliveryAddress' }) }} />
          {showDeliveryAddresses && !createAddressView ? (
            <DeliveryAddresses
              addresses={user?.details?.deliveryAddresses || []}
              onCreate={() => setCreateAddressView(true)}
              setAddress={completeDelivery}
              isLoading={state.status === ActionStatus.PENDING}
            />
          ) : (
            <DeliveryAddressForm
              instructions={order.deliveryInstructions}
              getDetailsPlaceData={getDetailsPlaceData}
              setCreateAddressView={setCreateAddressView}
              showDeliveryAddresses={showDeliveryAddresses}
              user={user}
              setAddress={completeDelivery}
              isLoading={state.status === ActionStatus.PENDING}
            />
          )}
        </>
      )}
      <AlertDialog {...(alertMessage || {})} />
    </>
  );
};
